import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { LicenseTypesActions } from "../_actions/LicenseTypesActions";
import { LocationsActions } from "../_actions/LocationsActions";
import { BudgetsStore } from "../_reducers/budgetsRootReducer";
import { genericActions } from "../_helpers/ActionErrorWrapper";
import { ContentSpace, MainPageWithButtons, CapsuleButton, WaitSpinner } from "../_components";
import { CustomSearchBar } from "../../Common/_components/CustomSearchBar";
import { LocationDetailView } from "./LocationDetailView";
import { LocationSearchResultsTable } from "./LocationSearchResultsTable";

const LOCATION_NOT_EXIST_ERROR_CODE = "201";

export function LocationSearchModule() {

    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //GLOBAL STATE
    const licenseTypes = useSelector(BudgetsStore.selectors.getLicenseTypes);

    //LOCAL STATE
    const [searchTerm, setSearchTerm] = useState("");
    const [locations, setLocations] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const detailRequestId = useRef(0);

    //INITIALIZATION
    useEffect(() => {
        dispatch(LicenseTypesActions.listLicenseTypes());
    }, [dispatch]);

    //ACTION HANDLERS
    const loadLocationDetail = useCallback(async (locationId) => {
        // Only the latest request counts: a slower answer to an earlier click
        // or a previous search must not replace what is on screen
        const requestId = ++detailRequestId.current;
        try {
            const detail = await dispatch(LocationsActions.getLocationById(locationId));
            if (detail == null || requestId !== detailRequestId.current)
                return;
            setSelectedLocation(detail);
            // The row shows the active licenses, which may have just changed
            setLocations(current => current && current.map(
                location => location.locationId === detail.locationId ? detail : location
            ));
        } catch (error) {
            dispatch(genericActions.errorCatch(error));
        }
    }, [dispatch]);

    async function handleSearch() {
        const trimmedTerm = searchTerm.trim();
        if (!trimmedTerm || isSearching)
            return;

        setIsSearching(true);
        setNotFound(false);
        setLocations(null);
        setSelectedLocation(null);
        detailRequestId.current++;

        try {
            if (/^\d+$/.test(trimmedTerm)) {
                await searchById(trimmedTerm);
            } else if (trimmedTerm.includes("@")) {
                await searchByAdminEmail(trimmedTerm);
            } else {
                // Neither a numeric id nor an email: the backend routes would
                // 404 outside the API error format, so short-circuit it here
                setNotFound(true);
            }
        } catch (error) {
            if (String(error.name) === LOCATION_NOT_EXIST_ERROR_CODE) {
                setNotFound(true);
            } else {
                dispatch(genericActions.errorCatch(error));
            }
        } finally {
            setIsSearching(false);
        }
    }

    async function searchById(locationId) {
        // The generic wrapper resolves to undefined on REQUEST_EXPIRED;
        // keep the "no search yet" state instead of rendering a table.
        const foundLocation = await dispatch(
            LocationsActions.getLocationById(locationId)
        );
        setLocations(foundLocation != null ? [foundLocation] : null);
    }

    async function searchByAdminEmail(email) {
        const foundLocations = await dispatch(
            LocationsActions.getLocationsByAdminEmail(email)
        );
        if (foundLocations == null)
            return;
        if (foundLocations.length === 0) {
            setNotFound(true);
            return;
        }
        setLocations(foundLocations);
    }

    function handleKeyDown(e) {
        if (e.key === "Enter")
            handleSearch();
    }

    //VIEW
    return (
        <ContentSpace
            // The location's tables need the whole width: next to the search
            // card they would be left with half the screen
            mainContent={selectedLocation ? null : (
                <MainPageWithButtons
                    pageTitle={t("mod_location_search_title")}
                    content={content()}
                    size={'X_LARGE'}
                />
            )}
            detailedContent={selectedLocation && (
                <LocationDetailView
                    key={selectedLocation.locationId}
                    location={selectedLocation}
                    licenseTypes={licenseTypes}
                    onChange={() => loadLocationDetail(selectedLocation.locationId)}
                />
            )}
            onGoBack={() => setSelectedLocation(null)}
        />
    );

    function content() {
        return (
            <div className="w-100">
                <div className="d-flex align-items-center mb-4" onKeyDown={handleKeyDown}>
                    <CustomSearchBar
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={t("mod_location_search_placeholder")}
                        className="flex-grow-1 mr-3"
                    />
                    <CapsuleButton
                        onClick={handleSearch}
                        text={t("mod_device_search_button")}
                        isEnabled={searchTerm.trim() !== "" && !isSearching}
                    />
                </div>

                {searchResult()}
            </div>
        );
    }

    function searchResult() {
        if (isSearching)
            return <WaitSpinner />;

        if (notFound)
            return <div className="alert alert-warning">{t("mod_location_search_not_found")}</div>;

        if (locations == null)
            return null;

        return (
            <LocationSearchResultsTable
                locations={locations}
                onSelectLocation={location => loadLocationDetail(location.locationId)}
            />
        );
    }
}
